import axios from 'axios'
 
import axiosInstance from 'api/axiosInstance'
import { addToast } from 'components/Toast/Toast'
import { clearAllCachesForFan, clearAllCachesForModel } from 'helpers/clearAllCacheStores'
import { type UserProfile } from 'models'
import { type GoogleCallbackPayload } from 'types/api/auth'
import { type quickRegistration, type userLogin } from 'types/types'
import { useLocalStorageClear, useLocalStorageSetAccessToken } from 'utils'
 
const apiUrl = process.env.REACT_APP_API_URL
 
const getDeviceId = () => {
    let deviceId = localStorage.getItem('device_id')

    if (!deviceId) {
        deviceId = crypto.randomUUID()
        localStorage.setItem('device_id', deviceId)
    }

    return deviceId
}


export const registerUser = async (
    data: quickRegistration
): Promise<UserProfile | null> => {
    const resp = await axios.post(`${apiUrl}/api/register`, {
        ...data,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })

    // Pre-registration response: no authentication token.
    if (
        resp.status === 201 &&
        resp.data?.success === true &&
        resp.data?.message === 'Pre-registration received.'
    ) {
        return null
    }

    // Normal registration response.
    useLocalStorageSetAccessToken(resp.data.data.token)

    return resp.data.data.userProfile
}
export type TwoFactorType = 'qr_authenticator' | 'sms'

export type LoginTwoFactorResponse = {
  requires_two_factor: true
  challenge: string
  two_factor_auth_type: TwoFactorType
  message?: string
}

export type LoginSuccessResponse = {
  requires_two_factor?: false
  token: string
  userProfile: UserProfile
  message?: string
}

export type LoginResponse =
  | LoginTwoFactorResponse
  | LoginSuccessResponse

export const loginUser = async (
  { email, password, rememberMe }: userLogin
): Promise<LoginResponse> => {
  const resp = await axios.post(`${apiUrl}/api/login`, {
    email,
    password,
    rememberMe,
    device_id: getDeviceId()
  })

  const data = resp.data?.data ?? resp.data

  const twoFactorPayload =
    data?.message &&
    typeof data.message === 'object' &&
    data.message.requires_two_factor === true
      ? data.message
      : null

  if (twoFactorPayload) {
    return {
      requires_two_factor: true,
      challenge: twoFactorPayload.challenge,
      two_factor_auth_type: twoFactorPayload.two_factor_auth_type,
      message: twoFactorPayload.message
    }
  }

  return {
    requires_two_factor: false,
    token: data.token,
    userProfile: data.userProfile,
    message: data.message
  }
}
export const logoutUser = async (
  token: string
): Promise<boolean> => {
  try {
    await axios.post(
      `${apiUrl}/api/logout`,
      {},
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    )

    return true
  } catch (error) {
    console.warn(
      'Backend logout cleanup failed:',
      error
    )

    return false
  }
}
export const logoutUserAndRedirect = async (role: 'fan' | 'model', redirectUrl?: string) => {
    let res
    try {
        res = await axiosInstance({
            method: 'post',
            url: '/api/logout'
        })
        if (role === 'model') {
            clearAllCachesForModel()
        } else {
            clearAllCachesForFan()
        }
        useLocalStorageClear()
        window.location.href = redirectUrl ?? '/'
    } catch (err: any) {
        if (err.response?.data.message === 'Unauthenticated.') {
            if (role === 'model') {
                clearAllCachesForModel()
            } else {
                clearAllCachesForFan()
            }
            useLocalStorageClear()
            window.location.href = redirectUrl ?? '/'
        }
 
        console.error(err)
    }
 
    return res
}

 
export const sendPasswordResetEmail = async (val: { email: string }) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/forgot-password',
        data: val
    })
    return data
}
export const resendSecurityRecovery = async (val: {
    email: string
}) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/security-recovery/resend',
        data: val
    })

    return data
}
export const resetPassword = async (val: {
    token: string
    email: string
    password: string
    password_confirmation: string
}) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/reset-password',
        data: val
    })
    return data
}

export const setPassword = async (val: {
    password: string
    password_confirmation: string
}) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/set-password',
        data: val
    })
    return data
}
 
export const disconnectSocialMedia = async (socialAccount: string , email? : string) => {
    let res
    try {
        res = await axiosInstance({
            method: 'post',
            url: 'api/user/disconnect-social-account',
            data: { social_account: socialAccount , new_email: email}
        })
    } catch (err: any) {
        console.error(err)
        addToast('error', 'This Email is Not Available!')
    }
 
    return res
}
 
export const googleCallback = async (
    payload: GoogleCallbackPayload
): Promise<{
    token: string
    userProfile: UserProfile
}> => {
    const resp = await axios.post(
        `${apiUrl}/api/google-callback`,
        payload
    )

    return {
        token: resp.data.data.token,
        userProfile: resp.data.data.userProfile
    }
}
 
export const getTwitterLoginLink = async (loginNonce: string) => {
    const { data } = await axiosInstance({
        method: 'get',
        url: '/api/twitter',
        params: {
            login_nonce: loginNonce
        }
    })

    return data
}
 
export const twitterApiCallback = async (reqData: {
  oauth_token: string
  oauth_verifier: string
}): Promise<{
  token: string
  userProfile: UserProfile
}> => {
  const resp = await axios.get(
    `${apiUrl}/api/twitter-callback`,
    {
      params: {
        oauth_token: reqData.oauth_token,
        oauth_verifier: reqData.oauth_verifier
      }
    }
  )

  return {
    token: resp.data.data.token,
    userProfile: resp.data.data.userProfile
  }
}
 
export const twitterConnect = async (reqData: {
    oauth_token: string
    oauth_verifier: string
}) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/user/twitter',
        data: {
            oauth_token: reqData.oauth_token,
            oauth_verifier: reqData.oauth_verifier
        }
    })
    return data
}
 
export const getGoogleLoginLink = async () => {
    const { data } = await axiosInstance<string>({
        method: 'get',
        url: '/api/redirect_to_google'
    })
    return data
}
 
export const googleConnect = async (reqData: { email: string }) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/user/google',
        data: {
            email: reqData.email
        }
    })
    return data
}
 export const getTwitterConnectLink = async () => {
    const { data } = await axiosInstance({
        method: 'get',
        url: '/api/user/twitter/connect-url'
    })

    return data
}


export const getGoogleConnectLink = async () => {
    const { data } = await axiosInstance({
        method: 'get',
        url: '/api/user/google/connect-url'
    })

    return data
}

export const googleConnectCode = async (reqData: {
    code: string
    state: string
}) => {
    const { data } = await axiosInstance({
        method: 'post',
        url: '/api/user/google/connect-code',
        data: {
            code: reqData.code,
            state: reqData.state
        }
    })

    return data
}


export const verifyEmail = async (
    userId: string,
    token: string,
    expires: number,
    signature: string
) => {
    const { data } = await axiosInstance({
        method: 'get',
        url: `/api/email/verify/${userId}/${token}?expires=${expires}&signature=${signature}`
    })
    return data
}